import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  FolderPlus,
  Home,
  Users,
  Settings,
  Wallet,
  Zap,
  ArrowLeftRight,
  FileText,
  CreditCard,
  TrendingUp,
  Landmark,
  User,
  LogOut,
  BarChart,
  Shield,
  ClipboardCheck,
  UserCheck,
  Receipt,
  Palette,
  Bell,
  Lock,
} from 'lucide-react';

interface MenuSection {
  id: string;
  label: string;
  order: number;
  collapsible: boolean;
  defaultExpanded?: boolean;
}

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  order: number;
  visible: boolean;
  sectionId?: string;
}

interface MenuConfig {
  sections: MenuSection[];
  items: MenuItem[];
}

interface MenuConfiguratorProps {
  config: MenuConfig;
  onChange: (config: MenuConfig) => void;
  userType: string;
  isLoading?: boolean;
}

const ICON_MAP: Record<string, React.ElementType> = {
  Home, Users, Settings, Wallet, Zap, ArrowLeftRight, FileText,
  CreditCard, TrendingUp, Landmark, User, LogOut, BarChart, Shield,
  ClipboardCheck, UserCheck, Receipt, Palette, Bell, Lock,
  LayoutDashboard: Home,
};

const AVAILABLE_ICONS = [
  'Home', 'Users', 'Settings', 'Wallet', 'Zap', 'ArrowLeftRight',
  'FileText', 'CreditCard', 'TrendingUp', 'Landmark', 'User', 'LogOut',
  'BarChart', 'Shield', 'ClipboardCheck', 'UserCheck', 'Receipt', 'Palette',
  'Bell', 'Lock',
];

// Essential items that cannot be hidden
const ESSENTIAL_ITEMS = ['logout', 'profile'];

function SortableMenuItem({
  item,
  onToggleVisibility,
  onRemove,
  onIconChange,
  isEssential,
}: {
  item: MenuItem;
  onToggleVisibility: () => void;
  onRemove: () => void;
  onIconChange: (icon: string) => void;
  isEssential: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const [showIconPicker, setShowIconPicker] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = ICON_MAP[item.icon] || Home;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-3 p-3 rounded-lg border transition-all
        ${item.visible
          ? 'bg-surface-700 border-surface-600'
          : 'bg-surface-800 border-surface-700 opacity-60'
        }
        ${isDragging ? 'ring-2 ring-primary-500' : ''}
      `}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 text-surface-400 hover:text-white cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <div className="relative">
        <button
          onClick={() => setShowIconPicker(!showIconPicker)}
          className="p-2 bg-surface-600 rounded-lg hover:bg-surface-500 transition-colors"
        >
          <Icon className="w-4 h-4 text-primary-400" />
        </button>
        
        {showIconPicker && (
          <div className="absolute top-full left-0 mt-2 p-2 bg-surface-700 rounded-lg shadow-xl border border-surface-600 z-50 grid grid-cols-5 gap-1 w-48">
            {AVAILABLE_ICONS.map((iconName) => {
              const IconOption = ICON_MAP[iconName] || Home;
              return (
                <button
                  key={iconName}
                  onClick={() => {
                    onIconChange(iconName);
                    setShowIconPicker(false);
                  }}
                  className={`
                    p-2 rounded hover:bg-surface-600 transition-colors
                    ${item.icon === iconName ? 'bg-primary-500/20 text-primary-400' : 'text-surface-300'}
                  `}
                >
                  <IconOption className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-medium text-white text-sm truncate">{item.label}</div>
        <div className="text-xs text-surface-400 truncate">{item.path}</div>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={onToggleVisibility}
          disabled={isEssential && item.visible}
          className={`
            p-1.5 rounded transition-colors
            ${isEssential && item.visible
              ? 'text-surface-500 cursor-not-allowed'
              : item.visible
                ? 'text-green-400 hover:bg-surface-600'
                : 'text-surface-400 hover:bg-surface-600'
            }
          `}
          title={isEssential ? 'Item essencial não pode ser ocultado' : item.visible ? 'Ocultar' : 'Mostrar'}
        >
          {item.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        
        {!isEssential && (
          <button
            onClick={onRemove}
            className="p-1.5 text-surface-400 hover:text-red-400 hover:bg-surface-600 rounded transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function SectionHeader({
  section,
  isExpanded,
  onToggle,
  onRemove,
  itemCount,
}: {
  section: MenuSection;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  itemCount: number;
}) {
  return (
    <div className="flex items-center gap-2 py-2">
      <button
        onClick={onToggle}
        className="p-1 text-surface-400 hover:text-white transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>
      <span className="font-medium text-surface-300 text-sm flex-1">
        {section.label}
      </span>
      <span className="text-xs text-surface-500">{itemCount} itens</span>
      <button
        onClick={onRemove}
        className="p-1 text-surface-400 hover:text-red-400 transition-colors"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

export function MenuConfigurator({
  config,
  onChange,
  userType,
  isLoading,
}: MenuConfiguratorProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(config.sections.filter(s => s.defaultExpanded !== false).map(s => s.id))
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemPath, setNewItemPath] = useState('');
  const [newSectionLabel, setNewSectionLabel] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = config.items.findIndex((item) => item.id === active.id);
      const newIndex = config.items.findIndex((item) => item.id === over.id);
      
      const newItems = arrayMove(config.items, oldIndex, newIndex).map((item, index) => ({
        ...item,
        order: index,
      }));
      
      onChange({ ...config, items: newItems });
    }
  };

  const toggleVisibility = (itemId: string) => {
    const item = config.items.find(i => i.id === itemId);
    if (!item) return;
    
    // Prevent hiding essential items
    if (ESSENTIAL_ITEMS.includes(itemId) && item.visible) return;

    onChange({
      ...config,
      items: config.items.map((i) =>
        i.id === itemId ? { ...i, visible: !i.visible } : i
      ),
    });
  };

  const removeItem = (itemId: string) => {
    if (ESSENTIAL_ITEMS.includes(itemId)) return;
    onChange({
      ...config,
      items: config.items.filter((i) => i.id !== itemId),
    });
  };

  const changeIcon = (itemId: string, icon: string) => {
    onChange({
      ...config,
      items: config.items.map((i) =>
        i.id === itemId ? { ...i, icon } : i
      ),
    });
  };

  const addItem = () => {
    if (!newItemLabel.trim() || !newItemPath.trim()) return;
    
    const newItem: MenuItem = {
      id: `custom-${Date.now()}`,
      label: newItemLabel,
      icon: 'Home',
      path: newItemPath,
      order: config.items.length,
      visible: true,
    };
    
    onChange({
      ...config,
      items: [...config.items, newItem],
    });
    
    setNewItemLabel('');
    setNewItemPath('');
  };

  const addSection = () => {
    if (!newSectionLabel.trim()) return;
    
    const newSection: MenuSection = {
      id: `section-${Date.now()}`,
      label: newSectionLabel,
      order: config.sections.length,
      collapsible: true,
      defaultExpanded: true,
    };
    
    onChange({
      ...config,
      sections: [...config.sections, newSection],
    });
    
    setNewSectionLabel('');
    setExpandedSections(prev => new Set([...prev, newSection.id]));
  };

  const removeSection = (sectionId: string) => {
    onChange({
      ...config,
      sections: config.sections.filter((s) => s.id !== sectionId),
      items: config.items.map((i) =>
        i.sectionId === sectionId ? { ...i, sectionId: undefined } : i
      ),
    });
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // Group items by section
  const itemsBySection = config.items.reduce((acc, item) => {
    const sectionId = item.sectionId || 'unsectioned';
    if (!acc[sectionId]) acc[sectionId] = [];
    acc[sectionId].push(item);
    return acc;
  }, {} as Record<string, MenuItem[]>);

  // Sort items within each section
  Object.values(itemsBySection).forEach((items) => {
    items.sort((a, b) => a.order - b.order);
  });

  const activeItem = activeId ? config.items.find((i) => i.id === activeId) : null;

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-surface-700 rounded w-1/4" />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-14 bg-surface-700 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white">Configuração de Menu</h3>
        <p className="text-sm text-surface-400">
          Arraste para reordenar, oculte ou adicione itens ao menu
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Menu Items */}
        <div className="lg:col-span-2 space-y-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={config.items.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              {/* Sectioned items */}
              {config.sections.map((section) => {
                const sectionItems = itemsBySection[section.id] || [];
                const isExpanded = expandedSections.has(section.id);
                
                return (
                  <div key={section.id} className="bg-surface-800 rounded-lg p-3">
                    <SectionHeader
                      section={section}
                      isExpanded={isExpanded}
                      onToggle={() => toggleSection(section.id)}
                      onRemove={() => removeSection(section.id)}
                      itemCount={sectionItems.length}
                    />
                    {isExpanded && (
                      <div className="space-y-2 mt-2">
                        {sectionItems.map((item) => (
                          <SortableMenuItem
                            key={item.id}
                            item={item}
                            onToggleVisibility={() => toggleVisibility(item.id)}
                            onRemove={() => removeItem(item.id)}
                            onIconChange={(icon) => changeIcon(item.id, icon)}
                            isEssential={ESSENTIAL_ITEMS.includes(item.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Unsectioned items */}
              {(itemsBySection['unsectioned'] || []).length > 0 && (
                <div className="bg-surface-800 rounded-lg p-3">
                  <div className="text-sm font-medium text-surface-400 mb-2">
                    Sem seção
                  </div>
                  <div className="space-y-2">
                    {(itemsBySection['unsectioned'] || []).map((item) => (
                      <SortableMenuItem
                        key={item.id}
                        item={item}
                        onToggleVisibility={() => toggleVisibility(item.id)}
                        onRemove={() => removeItem(item.id)}
                        onIconChange={(icon) => changeIcon(item.id, icon)}
                        isEssential={ESSENTIAL_ITEMS.includes(item.id)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </SortableContext>

            <DragOverlay>
              {activeItem && (
                <div className="bg-surface-700 rounded-lg p-3 border-2 border-primary-500 shadow-xl">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const Icon = ICON_MAP[activeItem.icon] || Home;
                      return <Icon className="w-4 h-4 text-primary-400" />;
                    })()}
                    <span className="font-medium text-white text-sm">
                      {activeItem.label}
                    </span>
                  </div>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        </div>

        {/* Add New */}
        <div className="space-y-4">
          {/* Add Item */}
          <div className="bg-surface-800 rounded-lg p-4">
            <h4 className="font-medium text-white mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Adicionar Item
            </h4>
            <div className="space-y-3">
              <input
                type="text"
                value={newItemLabel}
                onChange={(e) => setNewItemLabel(e.target.value)}
                placeholder="Nome do item"
                className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:border-primary-500 focus:outline-none"
              />
              <input
                type="text"
                value={newItemPath}
                onChange={(e) => setNewItemPath(e.target.value)}
                placeholder="/caminho/do/item"
                className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:border-primary-500 focus:outline-none"
              />
              <button
                onClick={addItem}
                disabled={!newItemLabel.trim() || !newItemPath.trim()}
                className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Adicionar Item
              </button>
            </div>
          </div>

          {/* Add Section */}
          <div className="bg-surface-800 rounded-lg p-4">
            <h4 className="font-medium text-white mb-3 flex items-center gap-2">
              <FolderPlus className="w-4 h-4" />
              Adicionar Seção
            </h4>
            <div className="space-y-3">
              <input
                type="text"
                value={newSectionLabel}
                onChange={(e) => setNewSectionLabel(e.target.value)}
                placeholder="Nome da seção"
                className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white text-sm focus:border-primary-500 focus:outline-none"
              />
              <button
                onClick={addSection}
                disabled={!newSectionLabel.trim()}
                className="w-full px-4 py-2 bg-surface-600 text-white rounded-lg hover:bg-surface-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Adicionar Seção
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="bg-surface-800/50 rounded-lg p-4 text-sm text-surface-400">
            <p className="mb-2">
              <strong className="text-surface-300">Dica:</strong> Itens essenciais como
              "Perfil" e "Sair" não podem ser ocultados.
            </p>
            <p>
              Clique no ícone de um item para alterá-lo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MenuConfigurator;
